import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'

// Mantine components
import { Loader, Tooltip, Button, Divider, Select, Switch } from '@mantine/core'

// Custom components
import ShopifyVariant from '@/components/shopify/variants/ShopifyVariant'
import LocationSelect from '@/components/ui/LocationSelect'
import ScrollableScreen from '@/components/ui/ScrollableScreen'

// UI
import Box from '@/components/ui/Box'
import Badge from '@/components/ui/Badge'
import ProductCollectionsSelect from '@/components/products/ProductCollectionsSelect'

// API
import { useAxiosApi } from '@/hooks/useAxiosApi'

export default function ShopifyProductPage() {
    const { product_id } = useParams()

    // Init states
    const [product, setProduct] = useState(null)
    const [location, setLocation] = useState('6')

    const [collectionsIds, setCollectionsIds] = useState([])
    const [visibleByRetailers, setVisibleByRetailers] = useState(false)
    const [shouldPushInventoryToShopify, setShouldPushInventoryToShopify] =
        useState(false)

    // Load product
    const { requestGet, requestPost, loading } = useAxiosApi()

    const fetchProduct = async () => {
        const { data } = await requestGet(`/shopify/products/${product_id}`)

        setProduct(data)
        setCollectionsIds(data.collections.map((c) => c.id.toString()))

        setVisibleByRetailers(data.visible_by_retailers)
        setShouldPushInventoryToShopify(data.should_push_inventory_to_shopify)
    }

    useEffect(() => {
        if (product_id) {
            fetchProduct()
        }
    }, [product_id])

    // Breadcumbs
    const breadcrumbs = [
        {
            label: 'Produits Shopify',
            href: '/products/shopify?status=ACTIVE',
        },
        {
            label: product?.title,
        },
    ]

    const componentsCount = {}

    product?.variants.forEach((variant) => {
        // Count components
        variant.components.forEach((component) => {
            if (componentsCount[component.id]) {
                componentsCount[component.id].count++
            } else {
                componentsCount[component.id] = {
                    component,
                    count: 1,
                }
            }
        })
    })

    // Sort components by count desc and name asc
    const sortedComponents = Object.values(componentsCount).sort((a, b) => {
        const full_name_a =
            a.component.component_category.name_fr + ' ' + a.component.name

        const full_name_b =
            b.component.component_category.name_fr + ' ' + b.component.name

        if (a.count > b.count) {
            return -1
        }

        if (a.count < b.count) {
            return 1
        }

        if (full_name_a < full_name_b) {
            return -1
        }

        if (full_name_a > full_name_b) {
            return 1
        }

        return 0
    })

    // View
    const [view, setView] = useState('bundles')

    // Sort variant by position
    const sortedVariants = product?.variants
        .filter((v) => !v.deleted_at)
        .sort((a, b) => a.position - b.position)

    // Status
    const statuses = {
        ARCHIVED: {
            label: 'Archivé',
            color: 'gray',
        },
        DRAFT: {
            label: 'Brouillon',
            color: 'orange',
        },
        ACTIVE: {
            label: 'Actif',
            color: 'green',
        },
    }

    // Change product infos


    const onCollectionsIdsChange = async (values) => {
        setCollectionsIds(values)

        await requestPost(`/shopify/${product_id}/edit`, {
            collections: values,
        })
    }

    const onVisibleByRetailersChange = async (value) => {
        setVisibleByRetailers(value === 'true')

        await requestPost(`/shopify/${product_id}/edit`, {
            visible_by_retailers: value === 'true' ? 'true' : 'false',
        })
    }

    // should_push_inventory_to_shopify
    const onShouldPushInventoryToShopifyChange = async (event) => {
        const checked = event.currentTarget.checked
        setShouldPushInventoryToShopify(checked)

        await requestPost(`/shopify/${product_id}/edit`, {
            should_push_inventory_to_shopify: checked,
        })
    }

    if (!product) {
        return null
    }

    const status = statuses[product.status]

    const rightSection = (
        <div className="flex gap-2">
            <Badge color={status.color}>{status.label}</Badge>

            <Select
                w={250}
                allowDeselect={false}
                data={[
                    { label: 'Bundles de composants', value: 'bundles' },
                    { label: 'Watch Clock Details', value: 'wcd' },
                    {
                        label: 'Prix de vente',
                        value: 'price',
                    },
                ]}
                value={view}
                onChange={setView}
            />
        </div>
    )

    return (
        <ScrollableScreen breadcrumbs={breadcrumbs} rightSection={rightSection}>
            {product && (
                <div className="p-5 max-w-[1200px] w-full mx-auto flex flex-col gap-5 @container">
                    <Box
                        title={product.title}
                        rightSection={loading && <Loader size="xs" />}
                    >
                        <div className="flex flex-col @3xl:grid @3xl:grid-cols-2 gap-5">
                            <Select
                                label="Visible par les revendeurs"
                                allowDeselect={false}
                                value={visibleByRetailers ? 'true' : 'false'}
                                onChange={onVisibleByRetailersChange}
                                data={[
                                    { label: 'Oui', value: 'true' },
                                    { label: 'Non', value: 'false' },
                                ]}
                            />

                            <ProductCollectionsSelect
                                label="Collections"
                                value={collectionsIds}
                                onChange={onCollectionsIdsChange}
                            />

                            <Switch
                                label="Envoyer l'inventaire BO à Shopify"
                                checked={shouldPushInventoryToShopify}
                                onChange={onShouldPushInventoryToShopifyChange}
                            />
                        </div>
                    </Box>

                    <Box title={'Variantes (' + sortedVariants.length + ')'}>
                        <LocationSelect
                            value={location}
                            onChange={setLocation}
                            label="Emplacement de stock"
                        />

                        <div className="flex flex-col">
                            {false && (
                                <Tooltip label="Recalculer les quantités disponibles à la vente pour toutes les variantes">
                                    <Button fullWidth mt="md">
                                        Recalculer les quantités
                                    </Button>
                                </Tooltip>
                            )}

                            <div class="flex flex-col gap-5 mt-10">
                                <div className="flex flex-col gap-10">
                                    {sortedVariants.map((variant, index) => (
                                        <>
                                            {index !== 0 && (
                                                <Divider variant="dashed" />
                                            )}

                                            <ShopifyVariant
                                                variant={variant}
                                                product={product}
                                                locationId={location}
                                                view={view}
                                                sortedComponents={
                                                    sortedComponents
                                                }
                                                fetchProduct={fetchProduct}
                                            />
                                        </>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </Box>
                </div>
            )}
        </ScrollableScreen>
    )
}
